/**
 * Leest de titel en de gekoppelde effecten van een doel van een deelnemer
 * (`PersonaGoals`), in welke opslagvorm het ook staat.
 *
 * Nodig omdat er drie vormen naast elkaar bestaan (gemeten 2026-10-20, 716
 * doelen) en elke lezer er tot nu toe één kende:
 * - huidig (sinds 2026-04): `Title` + `EffectIDs` (array) + `Status`
 * - ouder (2024-2025): `Goal` + `Effects` (array)
 * - oudst (2024-2026-03, verreweg de meeste): alleen `EffectId` (string) —
 *   het doel wás toen één effect, en het scherm toonde de effectnaam als titel.
 *
 * Er wordt niets gemigreerd: wie een oud doel bewerkt, schrijft `Title` en
 * `EffectIDs`, en die gaan dan voor.
 *
 * Spiegel: functions/personas/personaGoalFields.js, gepind door
 * personaGoalFields.parity.test.js.
 * AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
 */

/**
 * @typedef {object} PersonaGoal
 * @property {string} [Title] - Titel (huidige vorm)
 * @property {string} [Goal] - Titel (oudere vorm)
 * @property {Array<string>} [EffectIDs] - Gekoppelde effect-ID's (huidige vorm)
 * @property {Array<string>} [Effects] - Gekoppelde effect-ID's (oudere vorm)
 * @property {string} [EffectId] - Het ene effect (oudste vorm)
 */

/** @param {Array<*>} ids @returns {Array<string>} */
const cleanIds = (ids) => [...new Set(ids.filter((id) => typeof id === 'string' && id !== ''))]

/**
 * @param {PersonaGoal} [goal]
 * @returns {Array<string>} De gekoppelde effect-ID's, zonder lege of dubbele
 */
export const goalEffectIds = (goal) => {
    if (!goal) return []
    // Een bestaande EffectIDs wint, ook leeg: dat schreef het scherm het laatst.
    if (Array.isArray(goal.EffectIDs)) return cleanIds(goal.EffectIDs)
    if (Array.isArray(goal.Effects)) return cleanIds(goal.Effects)
    if (typeof goal.EffectId === 'string') return cleanIds([goal.EffectId])
    return []
}

/** @param {*} value @returns {string} */
const text = (value) => (typeof value === 'string' ? value.trim() : '')

/**
 * @param {PersonaGoal} [goal]
 * @param {Object<string, string>} [effectNameById] - Effectnaam per effect-ID
 * @returns {string} De titel, of '' als er geen is (of het effect nog laadt)
 */
export const goalTitle = (goal, effectNameById = {}) => {
    if (!goal) return ''
    const own = text(goal.Title) || text(goal.Goal)
    if (own) return own
    const ids = goalEffectIds(goal)
    return ids.length === 1 ? text(effectNameById[ids[0]]) : ''
}

const personaGoalFields = { goalEffectIds, goalTitle }

export default personaGoalFields
